import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { isEqual } from "lodash";
import { FunctionComponent, useEffect, useState } from "react";
import { TimeSheet, TimeSheetDay } from "../types/timesheet";
import HoursTimesheetDialog from "./HoursTimesheetDialog";

interface DayBoxProps {
  day: TimeSheetDay;
  variant: "small" | "large";
  index: number;
  onChange: (day: TimeSheetDay, index: number) => void;
  timesheet: TimeSheet;
  isDialogOpen?: boolean;
}

const DayBox: FunctionComponent<DayBoxProps> = (
  props: DayBoxProps
): JSX.Element => {
  const { day, variant, onChange, index, timesheet, isDialogOpen } = props;
  const [size] = useState<number>(variant === "small" ? 30 : 50);
  const [textSize] = useState<string>(variant === "small" ? "10px" : "12px");
  const [currentType, setCurrentType] = useState<number>(day.type);
  const [boxClicked, setBoxClicked] = useState<boolean>(false);
  const [chooseHours, setChooseHours] = useState<boolean>(false);
  const [newHours, setNewHours] = useState<number>(day.hour!);
  const [newMins, setNewMins] = useState<number>(day.minute!);
  const [originalType] = useState<number>(day.type);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    setCurrentType(day.type);
  }, [day.type]);

  const handleBoxClick = () => {
    if (
      (variant === "large" ||
        (isSmallScreen && variant === "small" && isDialogOpen)) &&
      currentType !== 0
    ) {
      setBoxClicked(true);

      let newtype = currentType + 1;
      if (newtype > 4) {
        newtype = 1;
      }

      if (originalType === newtype) {
        setBoxClicked(false);
      }
      setCurrentType(newtype);
      onChange(
        { id: day.id, type: newtype, hour: newHours, minute: newMins },
        index
      );
    } else if (
      (variant === "large" ||
        (isSmallScreen && variant === "small" && isDialogOpen)) &&
      currentType === 0
    ) {
      handleHourBoxClick();
    }
  };

  const handleHourBoxClick = () => {
    setChooseHours(true);
  };

  const handleHoursClose = () => {
    setChooseHours(false);
  };

  const handleSaveHours = (hours: number, minutes: number) => {
    setNewHours(hours);
    setNewMins(minutes);
    setBoxClicked(!(hours === day.hour && minutes === day.minute));
    onChange(
      { id: day.id, type: currentType, hour: hours, minute: minutes },
      index
    );
  };

  return (
    <>
      <Box
        width={size}
        height={size}
        component={
          !isSmallScreen && variant === "small"
            ? "section"
            : isSmallScreen && !isDialogOpen
            ? "section"
            : "button"
        }
        sx={{
          display: "flex",
          alignItems:
            currentType === 0 ? "center" : currentType === 1 ? "start" : "end",
          justifyContent:
            currentType === 0 ? "center" : currentType === 1 ? "start" : "end",
          border: "1px solid black",
          background:
            currentType === 0 || currentType === 3
              ? boxClicked ||
                (timesheet.status === 3 &&
                  !isEqual(
                    timesheet.days.find((d) => d.id === day.id),
                    timesheet.amendedDays.find((d) => d.id === day.id)
                  ))
                ? "rgba(239, 108, 0, 0.6)" // orange
                : "rgba(43, 135, 48, 0.4)" // green
              : currentType === 1
              ? boxClicked ||
                (timesheet.status === 3 &&
                  !isEqual(
                    timesheet.days.find((d) => d.id === day.id),
                    timesheet.amendedDays.find((d) => d.id === day.id)
                  ))
                ? "linear-gradient(135deg, rgba(239, 108, 0, 0.6) 50%, white 50%)"
                : "linear-gradient(135deg, rgba(43, 135, 48, 0.4) 50%, white 50%)"
              : currentType === 2
              ? boxClicked ||
                (timesheet.status === 3 &&
                  !isEqual(
                    timesheet.days.find((d) => d.id === day.id),
                    timesheet.amendedDays.find((d) => d.id === day.id)
                  ))
                ? "linear-gradient(135deg, white 50%, rgba(239, 108, 0, 0.6) 50%)"
                : "linear-gradient(135deg, white 50%, rgba(43, 135, 48, 0.4) 50%)"
              : "transparent",
          cursor: variant === "large" ? "pointer" : "default",
        }}
        onClick={handleBoxClick}
      >
        {currentType === 0 && (
          <Typography
            variant="caption"
            sx={{
              fontSize: textSize,
              textAlign: "center",
              lineHeight: "12px",
              p: "3px",
            }}
          >
            {newHours ?? day.hour}h {newMins ?? day.minute}m
          </Typography>
        )}
        {currentType === 1 && (
          <Typography variant="caption" sx={{ fontSize: textSize, pl: "3px" }}>
            {" "}
            AM{" "}
          </Typography>
        )}
        {currentType === 2 ? (
          <Typography variant="caption" sx={{ fontSize: textSize, pr: "3px" }}>
            {" "}
            PM{" "}
          </Typography>
        ) : (
          ""
        )}
      </Box>

      <HoursTimesheetDialog
        chooseHours={chooseHours}
        handleHoursClose={handleHoursClose}
        day={day}
        setBoxClicked={setBoxClicked}
        onSave={handleSaveHours}
        originalNewHours={newHours}
        originalNewMins={newMins}
      />
    </>
  );
};

export default DayBox;
